import React from "react";

/**
 * ResumeDark.js
 * Componente React funcional que renderiza el CV en modo "dark".
 *
 * Props:
 *  - avatar (string): URL de la imagen de avatar
 *  - name (string)
 *  - title (string)
 *  - contact (object): { phone, email, location }
 *  - links (object): { actasDemo, actasRepo, libroDemo, libroRepo, chatbotDemo, chatbotRepo }
 *
 * Ejemplo de uso:
 * <ResumeDark
 *   avatar="/assets/yeimy.png"
 *   name="Yeimy F. Méndez Mendoza"
 *   title="Desarrollador Full Stack"
 *   contact={{ phone: "301 631 4914 / 311 789 4435", email: "yeimyfmendez@gmail.com", location: "Popayán, Cauca – Colombia" }}
 *   links={{ actasDemo: "#", actasRepo: "#", libroDemo: "#", libroRepo: "#", chatbotDemo: "#", chatbotRepo: "#" }}
 * />
 */

export function portfolio({
  avatar = "assets/yeimy.png",
  name = "Yeimy F. Méndez Mendoza",
  title = "Desarrollador Full Stack",
  contact = {
    phone: "301 631 4914 / 311 789 4435",
    email: "yeimyfmendez@gmail.com",
    location: "Popayán, Cauca – Colombia",
  },
  links = {
    actasDemo: "#",
    actasRepo: "#",
    libroDemo: "#",
    libroRepo: "#",
    chatbotDemo: "#",
    chatbotRepo: "#",
  },
}) {
  return (
    <>
      <style>{`
        :root {
          --bg: #0d1117;
          --card: #0b1220;
          --muted: #8b949e;
          --text: #c9d1d9;
          --accent: #58a6ff;
          --border: #30363d;
          --pill-bg: rgba(88, 166, 255, 0.08);
        }
        html, body {
          height: 100%;
          margin: 0;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          color: var(--text);
          background: var(--bg);
        }
        .container {
          max-width: 900px;
          margin: 36px auto;
          padding: 20px;
        }
        .card {
          background: linear-gradient(180deg, var(--card), #07101a);
          border: 1px solid var(--border);
          border-radius: 8px;
          display: flex;
          overflow: hidden;
          box-shadow: 0 6px 18px rgba(2, 6, 23, 0.6);
        }
        .sidebar {
          width: 260px;
          padding: 22px;
          border-right: 1px solid rgba(255, 255, 255, 0.03);
        }
        .main {
          flex: 1;
          padding: 26px;
        }
        .name {
          font-size: 20px;
          font-weight: 700;
          color: var(--accent);
        }
        .title {
          font-size: 13px;
          color: var(--muted);
          margin-bottom: 12px;
        }
        .contact p {
          margin: 6px 0;
          font-size: 13px;
          color: var(--text);
        }
        .section {
          margin-bottom: 16px;
        }
        .section h3 {
          font-size: 14px;
          margin: 0 0 8px 0;
          color: var(--text);
        }
        .muted {
          color: var(--muted);
          font-size: 13px;
        }
        ul {
          margin: 6px 0 6px 18px;
        }
        .skill-pill {
          display: inline-block;
          padding: 6px 8px;
          margin: 4px 6px 0 0;
          border-radius: 20px;
          background: var(--pill-bg);
          color: var(--accent);
          font-weight: 600;
          font-size: 12px;
          border: 1px solid rgba(88, 166, 255, 0.12);
        }
        .job {
          margin-bottom: 12px;
        }
        .job .meta {
          font-size: 13px;
          color: var(--muted);
          margin-bottom: 6px;
        }
        .download {
          display: inline-block;
          padding: 8px 12px;
          background: var(--accent);
          color: #07101a;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
          cursor: pointer;
        }
        .avatar {
          display: flex;
          justify-content: center;
          margin: 12px 0 14px 0;
        }
        .avatar-img {
          width: 110px;
          height: 110px;
          object-fit: cover;
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.04);
          box-shadow: 0 6px 18px rgba(2, 6, 23, 0.6);
        }
        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 12px;
          margin-top: 8px;
        }
        .project-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.03);
          padding: 10px;
          border-radius: 8px;
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }
        .project-info h4 {
          margin: 0 0 4px 0;
          font-size: 13px;
          color: var(--text);
        }
        .project-info p {
          margin: 0;
          font-size: 13px;
          color: var(--muted);
        }
        .project-actions {
          margin-top: 8px;
          display: flex;
          gap: 8px;
        }
        .btn {
          padding: 6px 10px;
          border-radius: 6px;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.06);
          color: var(--text);
          font-weight: 600;
          text-decoration: none;
          font-size: 13px;
        }
        .btn.primary {
          background: var(--accent);
          color: #07101a;
          border-color: transparent;
        }

        @media (max-width: 700px) {
          .card {
            flex-direction: column;
          }
          .sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid rgba(255, 255, 255, 0.03);
          }
          .projects-grid {
            grid-template-columns: 1fr;
          }
        }

        @media print {
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { background: #0d1117 !important; color: #c9d1d9 !important; }
          .card { background: linear-gradient(180deg, #0b1220, #07101a) !important; border: 1px solid #30363d !important; box-shadow: none !important; }
          .download, .project-actions { display: none !important; }
          img { break-inside: avoid; }
        }
      `}</style>

      <div className="container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div>
            <button
              className="download"
              onClick={() => {
                window.print();
              }}
            >
              Imprimir / Guardar PDF
            </button>
          </div>
        </div>

        <div className="card">
          <aside className="sidebar">
            <div className="avatar">
              <img
                className="avatar-img"
                src={avatar}
                alt={`Foto de ${name}`}
                loading="lazy"
              />
            </div>

            <div className="name">{name}</div>
            <div className="muted">
              Tecnólogo en Análisis y Desarrollo de Sistemas · Procesamiento de
              Datos para Modelos de IA
            </div>
            <div className="title">{title}</div>

            <div className="section contact">
              <h3>Contacto</h3>
              <p>📞 {contact.phone}</p>
              <p>✉️ {contact.email}</p>
              <p>📍 {contact.location}</p>
            </div>

            <div className="section">
              <h3>Skills</h3>
              <div>
                <h4>Lenguajes y frameworks</h4>
                <span className="skill-pill">Python</span>
                <span className="skill-pill">JavaScript</span>
                <span className="skill-pill">TypeScript</span>
                <span className="skill-pill">Django</span>
                <span className="skill-pill">FastAPI</span>
                <span className="skill-pill">React</span>
                <span className="skill-pill">Next.js</span>

                <h4>Ciencia de Datos / IA</h4>
                <span className="skill-pill">Pandas</span>
                <span className="skill-pill">NumPy</span>
                <span className="skill-pill">Scikit-learn</span>
                <span className="skill-pill">TensorFlow</span>
                <span className="skill-pill">Keras</span>
                <span className="skill-pill">PyTorch</span>
                <span className="skill-pill">OpenCV</span>
                <span className="skill-pill">NLP</span>
                <span className="skill-pill">Entrenamiento de Modelos</span>
                <span className="skill-pill">Optimización de Modelos</span>
                <span className="skill-pill">Evaluación de Modelos</span>

                <h4>Procesamiento de Datos / Data Engineering</h4>
                <span className="skill-pill">ETL Pipelines</span>
                <span className="skill-pill">Data Warehouse</span>
                <span className="skill-pill">BigQuery</span>
                <span className="skill-pill">Apache Airflow</span>
                <span className="skill-pill">Power BI</span>

                <h4>Bases de datos</h4>
                <span className="skill-pill">PostgreSQL</span>
                <span className="skill-pill">SQL Server</span>
                <span className="skill-pill">MySQL</span>
                <span className="skill-pill">SQLite</span>
                <span className="skill-pill">MongoDB</span>

                <h4>Frontend y estilos</h4>
                <span className="skill-pill">HTML5</span>
                <span className="skill-pill">CSS3</span>
                <span className="skill-pill">Tailwind CSS</span>
                <span className="skill-pill">Bootstrap</span>

                <h4>DevOps / Herramientas</h4>
                <span className="skill-pill">Git</span>
                <span className="skill-pill">Docker</span>
                <span className="skill-pill">Nginx</span>
                <span className="skill-pill">Gunicorn</span>
                <span className="skill-pill">Heroku</span>
                <span className="skill-pill">Render</span>

                <span className="skill-pill">API REST</span>
                <span className="skill-pill">JWT</span>
                <span className="skill-pill">WebSockets</span>
                <span className="skill-pill">Testing</span>
                <span className="skill-pill">CI/CD</span>
              </div>
            </div>

            <div style={{ pageBreakBefore: "always", breakBefore: "page" }} />
            <br />
            <br />
            <br />
            <br />

            <div className="section">
              <h3>Herramientas</h3>
              <ul className="muted">
                <li>VS Code · PyCharm · Jupyter Notebook · Anaconda Navigator</li>
                <li>
                  Postman · Insomnia · DBeaver · DB Browser · MongoDB Compass ·
                  SSMS
                </li>
                <li>
                  Apache Airflow · Talend · Pentaho · Power BI · Tableau ·
                  Google Data Studio
                </li>
                <li>GitHub · GitLab · GitHub Actions · Docker Desktop</li>
                <li>Figma · Adobe XD · Illustrator · Photoshop</li>
                <li>Unity · OBS Studio · Google Colab · Kaggle</li>
              </ul>
            </div>

            <div className="section">
              <h3>Idiomas</h3>
              <p className="muted">Español (nativo) · Inglés (lectura y comprensión)</p>
            </div>

            <footer>
              <div className="muted">Referencias disponibles a solicitud</div>
            </footer>
          </aside>

          <main className="main">
            <br />
            <section className="section">
              <h3>Perfil profesional</h3>
              <p className="muted">
                Tecnólogo en Análisis y Desarrollo de Sistemas de Información
                (SENA) con experiencia en desarrollo de aplicaciones web y
                móviles, y Técnico en procesamiento de datos para modelos de
                inteligencia artificial. Docente y líder de proyectos con enfoque
                en soluciones escalables y prácticas de calidad de software.
              </p>
            </section>

            <section className="section">
              <h3>Experiencia</h3>

              <div className="job">
                <div style={{ fontWeight: 700 }}>Pasante Etapa Productiva – SENA CTPI</div>
                <div className="meta">
                  Centro de Teleinformática y Producción Industrial (CTPI) · Jun 2025 – Dic 2025
                </div>
                <ul>
                  <li>Participé en varios proyectos web que integraban modelos de inteligencia artificial para tareas de clasificación, búsqueda semántica y análisis de texto.</li>
                  <li>Colaboré en la preparación y limpieza de datasets, entrenamiento y evaluación de modelos de IA, y en la puesta en producción de microservicios de inferencia.</li>
                  <li>Desarrollé componentes frontend (React) y endpoints backend (Django) para integrar modelos ML/AI con aplicaciones web.</li>
                  <li>Trabajé con pipelines de datos, optimización de modelos y pruebas de rendimiento para despliegue en entornos productivos.</li>
                </ul>
              </div>

              <div className="job">
                <div style={{ fontWeight: 700 }}>
                  Docente de Programación, Análisis de Datos e Inteligencia Artificial
                </div>
                <div className="meta">Fundación Académica de Colombia – Instituto C.N.I · Nov 2023 – Jul 2024</div>
                <ul>
                  <li>Diseño e impartición de cursos prácticos en Python, IA, React y Django.</li>
                  <li>Desarrollo de contenidos y proyectos de IA aplicados.</li>
                </ul>
              </div>

              <div className="job">
                <div style={{ fontWeight: 700 }}>Soporte Técnico / Desarrollador Junior</div>
                <div className="meta">Grupo ASD · Sep 2023 – Nov 2023</div>
                <ul>
                  <li>Mantenimiento y depuración de aplicaciones en Python, Django y React.</li>
                  <li>Documentación técnica y soporte de despliegue.</li>
                </ul>
              </div>

              <div className="job">
                <div style={{ fontWeight: 700 }}>Analista de Datos / Tecnólogo ADSI</div>
                <div className="meta">ASMET SALUD EPS · Nov 2021 – May 2022</div>
                <ul>
                  <li>Participación en desarrollo de sistemas web y móviles; elaboración de manuales técnicos.</li>
                </ul>
              </div>

              <div className="job">
                <div style={{ fontWeight: 700 }}>Proyecto Formativo – SENA ADSI</div>
                <div className="meta">SENA · Apr 2020 – Nov 2021</div>
                <ul>
                  <li>Liderazgo y desarrollo de aplicación web y móvil utilizando Django y Laravel.</li>
                </ul>
              </div>
            </section>

            <section className="section">
              <h3>Formación</h3>
              <ul>
                <li><strong>Técnico</strong> — Procesamiento de Datos para Modelos de IA (SENA), 2025</li>
                <li><strong>Tecnólogo</strong> — Análisis y Desarrollo de Sistemas de Información (SENA), 2021</li>
                <li>Curso React y Django — Udemy, 2023</li>
              </ul>
            </section>

            <section className="section">
              <h3>Certificaciones</h3>
              <ul>
                <li>Competencia Laboral: Sistematizar datos masivos – SENA, 2024</li>
                <li>Bootcamp y Hackathon en IA – SENA, 2024</li>
                <li>Curso: Visualización de Datos en Python – SENA, 2021</li>
              </ul>
            </section>

            <section className="section">
              <h3>Proyectos relevantes</h3>
              <div className="projects-grid">
                <article className="project-card">
                  <div className="project-info">
                    <h4>Actas Inteligentes</h4>
                    <p className="muted">
                      Aplicación web para gestión y generación automática de actas
                      de reunión con integración de IA (transcripción de audio,
                      galería fotográfica y generación PDF). Desarrollada con
                      Django, React y modelo Whisper de OpenAI.
                    </p>
                    <div className="project-actions">
                      <a className="btn" href={links.actasDemo} target="_blank" rel="noreferrer">Demo</a>
                      <a className="btn" href={links.actasRepo} target="_blank" rel="noreferrer">Repositorio</a>
                    </div>
                  </div>
                </article>

                <article className="project-card">
                  <div className="project-info">
                    <h4>Libro Interactivo (Kivy)</h4>
                    <p className="muted">
                      Proyecto educativo en Python + Kivy que integra reconocimiento de voz para lectura guiada e interacción por comandos hablados.
                    </p>
                    <div className="project-actions">
                      <a className="btn" href={links.libroDemo} target="_blank" rel="noreferrer">Demo</a>
                      <a className="btn" href={links.libroRepo} target="_blank" rel="noreferrer">Repositorio</a>
                    </div>
                  </div>
                </article>

                <article className="project-card">
                  <div className="project-info">
                    <h4>Chatbot Avatar 3D</h4>
                    <p className="muted">
                      Asistente virtual con avatar 3D creado en Unity e integrado con IA conversacional.
                    </p>
                    <div className="project-actions">
                      <a className="btn" href={links.chatbotDemo} target="_blank" rel="noreferrer">Demo</a>
                      <a className="btn" href={links.chatbotRepo} target="_blank" rel="noreferrer">Repositorio</a>
                    </div>
                  </div>
                </article>

                <article className="project-card">
                  <div className="project-info">
                    <h4>Maracuba Jugos — App Cafetería / Restaurante (Django + React)</h4>
                    <p className="muted">
                      Aplicación administrativa y de venta para cafetería/restaurante. Gestión de pedidos, inventario y módulo de caja.
                    </p>
                  </div>
                </article>
              </div>
            </section>

            <section className="section">
              <h3>Competencias</h3>
              <ul>
                <li>Liderazgo y coordinación de equipos</li>
                <li>Resolución de problemas técnicos y depuración</li>
                <li>Comunicación técnica y documentación</li>
                <li>Ética profesional e innovación</li>
              </ul>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}
